import {
  Activity,
  ArrowDown,
  ArrowUp,
  Ban,
  Bell,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  Clock,
  Copy,
  CornerDownLeft,
  ExternalLink,
  FileText,
  Flame,
  Globe,
  Info,
  Inbox,
  LayoutDashboard,
  Mail,
  Megaphone,
  Moon,
  PanelLeft,
  Plus,
  RefreshCw,
  Reply,
  Rows3,
  SearchX,
  ScrollText,
  Server,
  Search,
  Settings,
  ShieldCheck,
  ShieldOff,
  Sun,
  Trash2,
  TriangleAlert,
  Unplug,
  Users,
  Webhook,
  Workflow,
  X,
  type LucideIcon,
} from 'lucide-react'

/**
 * The console's icon set — **Lucide**.
 *
 * The design's artboard shipped hand-drawn paths in Lucide's idiom (24×24, `fill: none`,
 * `stroke: currentColor`, round caps and joins). Those are replaced here by the real library: a
 * maintained set means the next screen that needs a glyph this list does not have gets a correct
 * one instead of a hand-drawn approximation that is subtly off-weight.
 *
 * Two things are kept from the design rather than taken from Lucide's defaults:
 *
 *   - **Stroke width.** Lucide ships at 2. The design draws at **1.55**, and 1.7 in the sidebar.
 *     At 13px body text a 2px stroke reads heavier than the type beside it, which is what makes an
 *     icon rail look bolted on.
 *   - **Size.** 15px default, not Lucide's 24.
 *
 * The indirection is worth keeping: screens name a *role* (`domains`, `suppressions`) rather than
 * a picture, so changing which glyph means "suppressions" is one line here and not a search across
 * seventeen files.
 */
const ICONS = {
  // Navigation — one per entry in `app/nav.ts`, in that order.
  overview: LayoutDashboard,
  mailboxes: Mail,
  replies: Reply,
  campaigns: Megaphone,
  contacts: Users,
  domains: Globe,
  sequences: Workflow,
  templates: FileText,
  warmup: Flame,
  deliverability: ShieldCheck,
  schedules: Clock,
  suppressions: Ban,
  observability: Activity,
  alerts: Bell,
  audit: ScrollText,
  webhooks: Webhook,
  // The platform plane — Axmion itself, not any one tenant.
  platform: Server,
  settings: Settings,

  // Chrome and controls.
  search: Search,
  chevronDown: ChevronDown,
  chevronRight: ChevronRight,
  chevronLeft: ChevronLeft,
  close: X,
  check: Check,
  plus: Plus,
  refresh: RefreshCw,
  external: ExternalLink,
  copy: Copy,
  trash: Trash2,
  sun: Sun,
  moon: Moon,
  rows: Rows3,
  panelLeft: PanelLeft,

  // States — every empty, denied, error and not-wired surface names one of these rather than
  // rendering a heading over nothing.
  warning: TriangleAlert,
  info: Info,
  error: CircleAlert,
  success: CircleCheck,
  empty: Inbox,
  noResults: SearchX,
  denied: ShieldOff,
  unwired: Unplug,
  help: CircleHelp,

  // Keyboard hints in the palette.
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,
  enter: CornerDownLeft,
} satisfies Record<string, LucideIcon>

export type IconName = keyof typeof ICONS

export function Icon({
  name,
  size = 15,
  className,
  strokeWidth = 1.55,
}: {
  name: IconName
  size?: number
  className?: string
  strokeWidth?: number
}) {
  const Glyph = ICONS[name]

  return (
    <Glyph
      size={size}
      strokeWidth={strokeWidth}
      className={className}
      // Decorative by default. Every place one of these carries meaning on its own gives the
      // surrounding control a real label instead.
      aria-hidden="true"
      focusable="false"
      style={{ flex: 'none' }}
    />
  )
}
